import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { personalAreaAuthState, personalAreaOrdersState, aboutUs } from '@atoms';
import { creditsBalanceState } from '@atoms/priceCalc';
import useWindowDimensions from '@helpers/hooks/windowDemensions';
import { useSecondModal } from '@helpers/hooks/useSecondModal';
import {
  getPersonalOrders,
  getPersonalOrderDetail,
  getCreditsBalance,
} from '@api';
import OrderList from './components/OrderList';
import OrderDetail from './components/OrderDetail';
import './style.css';

const getDaysFromFilter = (filter) => {
  if (filter >= 2000) {
    const start = new Date(filter, 0, 1);
    return Math.ceil((Date.now() - start.getTime()) / (24 * 60 * 60 * 1000));
  }
  return filter;
};

// Wall hero photo positions, in render order. The first photo gets the
// dominant center spot; remaining four scatter to the corners.
const WALL_POSITIONS = ['center', 'tl', 'tr', 'bl', 'br'];

// Frame PNG paths in /public. Set inline as background-image so webpack
// doesn't try to resolve absolute public-folder URLs from CSS as modules.
const FRAME_BG = {
  classic: '/assets/file/images/frame_classic.png',
  bold:    '/assets/file/images/frame_bold.png',
  ever:    '/assets/file/images/frame_even.png',
  clean:   '/assets/file/images/frame_clean.png',
  nordic:  '/assets/file/images/frame_nordic.png',
  village: '/assets/file/images/frame_village.png',
};

// Pull up to 5 framed photos for the wall hero.
// Strategy: one photo per recent order (variety of frames). If we don't
// have enough orders to reach 5, top up with extra thumbnails from the
// same orders so the wall doesn't look sparse.
const buildWallPhotos = (orders) => {
  const photos = [];
  for (const order of orders) {
    if (!Array.isArray(order.thumbnails) || !order.thumbnails.length) continue;
    photos.push({ src: order.thumbnails[0], frame: order.frameType });
    if (photos.length >= WALL_POSITIONS.length) return photos;
  }
  for (const order of orders) {
    if (!Array.isArray(order.thumbnails)) continue;
    for (let i = 1; i < order.thumbnails.length; i++) {
      photos.push({ src: order.thumbnails[i], frame: order.frameType });
      if (photos.length >= WALL_POSITIONS.length) return photos;
    }
  }
  return photos;
};

// Aggregate top-of-page stats from the orders list.
const buildHeroStats = (orders) => {
  const ordersCount = orders.length;
  const printsCount = orders.reduce(
    (sum, o) => sum + (parseInt(o.items, 10) || 0),
    0,
  );
  let earliestYear = new Date().getFullYear();
  for (const o of orders) {
    const m = String(o.date || '').match(/\b(20\d{2})\b/);
    if (m) {
      const y = parseInt(m[1], 10);
      if (y < earliestYear) earliestYear = y;
    }
  }
  return { ordersCount, printsCount, memberSince: earliestYear };
};

const PersonalArea = () => {
  const navigate = useNavigate();
  const { width } = useWindowDimensions();
  const modal = useSecondModal();
  const [auth, setAuth] = useRecoilState(personalAreaAuthState);
  const [orders, setOrders] = useRecoilState(personalAreaOrdersState);
  const [creditsBalance, setCreditsBalance] = useRecoilState(creditsBalanceState);
  const [, setAboutUs] = useRecoilState(aboutUs);
  const [view, setView] = useState('list');
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  // Initialize loading flags to true so the first render shows skeletons
  // instead of an empty / "חבר" fallback while the initial fetches are in flight.
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [creditsLoading, setCreditsLoading] = useState(true);
  const [ordersError, setOrdersError] = useState('');
  const [dateFilter, setDateFilter] = useState(90);

  // Restore auth from localStorage on mount
  useEffect(() => {
    const savedToken = localStorage.getItem('pa_token');
    const savedPhone = localStorage.getItem('pa_phone');
    if (savedToken && savedPhone && !auth.isVerified) {
      setAuth({ isVerified: true, phoneNumber: savedPhone, token: savedToken });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // .page-content-wrapper carries will-change:transform, which makes it the
  // containing block for EVERY position:fixed descendant (site header, edit
  // snackbar, sheets, toast) — they'd anchor to the page instead of the
  // viewport. The CSS-only fix uses body:has(.personal-area), but :has() is
  // missing on iOS Safari < 15.4, so ALSO toggle a body class from JS.
  useEffect(() => {
    document.body.classList.add('pa-open');
    return () => document.body.classList.remove('pa-open');
  }, []);

  // If not verified, redirect to home and open OTP modal
  useEffect(() => {
    if (!auth.isVerified) {
      const savedToken = localStorage.getItem('pa_token');
      if (!savedToken) {
        const modalName = width > 767 ? 'personalArea' : 'personalAreaMobile';
        modal('open', modalName);
        navigate('/', { replace: true });
      }
    }
  }, [auth.isVerified]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchOrders = useCallback(async (token, days) => {
    setOrdersLoading(true);
    setOrdersError('');
    try {
      const result = await getPersonalOrders(token, days);
      if (result.success) {
        setOrders(result.orders);
      } else {
        setOrdersError(result.error || 'שגיאה בטעינת ההזמנות');
      }
    } catch (err) {
      if (err.response?.status === 401) {
        handleLogout();
      } else {
        setOrdersError(err.response?.data?.error || 'שגיאה בטעינת ההזמנות, נסו שוב');
      }
    } finally {
      setOrdersLoading(false);
    }
  }, [setOrders]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (auth.isVerified && auth.token) {
      fetchOrders(auth.token, getDaysFromFilter(dateFilter));
    }
  }, [auth.isVerified, auth.token, dateFilter, fetchOrders]);

  // Fetch credits balance
  useEffect(() => {
    if (auth.isVerified && auth.token) {
      setCreditsLoading(true);
      getCreditsBalance(auth.token)
        .then(res => { if (res.success) setCreditsBalance(res.balance); })
        .catch(() => {})
        .finally(() => setCreditsLoading(false));
    }
  }, [auth.isVerified, auth.token, setCreditsBalance]);

  const handleLogout = () => {
    localStorage.removeItem('pa_token');
    localStorage.removeItem('pa_phone');
    setAuth({ isVerified: false, phoneNumber: '', token: '' });
    setOrders([]);
    navigate('/', { replace: true });
  };

  const handleSelectOrder = (orderId) => {
    setSelectedOrderId(orderId);
    setView('detail');
    window.scrollTo(0, 0);
  };

  const handleBackToList = () => {
    setView('list');
    setSelectedOrderId(null);
    if (auth.token) {
      fetchOrders(auth.token, getDaysFromFilter(dateFilter));
    }
  };

  const wallPhotos = useMemo(() => buildWallPhotos(orders), [orders]);
  const heroStats = useMemo(() => buildHeroStats(orders), [orders]);

  if (!auth.isVerified) {
    return null;
  }

  const firstName = orders.length > 0 && orders[0].customer
    ? orders[0].customer.split(' ')[0]
    : '';

  return (
    <div className="personal-area">
      <div className="pa-container">

        {view === 'list' && (
          <>
            {/* Compact top bar — greeting (mobile only) + sign out */}
            <div className="pa-user-bar">
              <button className="pa-user-bar__logout" onClick={handleLogout}>
                התנתק
              </button>
            </div>

            {/* Wall hero — greeting + stats on one side, floating framed photos on the other */}
            <section className="pa-wall-hero">
              <div className="pa-wall-hero__text">
                <h1 className="pa-wall-hero__title">
                  {ordersLoading ? (
                    <span className="pa-wall-hero__title-skel" aria-hidden="true" />
                  ) : (
                    <>היי, {firstName || 'חבר'}<span className="pa-sq" aria-hidden="true" /></>
                  )}
                </h1>
                <p className="pa-wall-hero__sub">
                  {ordersLoading ? (
                    <>
                      <span className="skeleton-line" style={{ display: 'block', width: '92%', height: 14, marginBottom: 8 }} />
                      <span className="skeleton-line" style={{ display: 'block', width: '68%', height: 14 }} />
                    </>
                  ) : heroStats.printsCount > 0 ? (
                    `התמונות, ההזמנות והרגעים שלך. הכל כאן, קרוב אלייך.`
                  ) : (
                    'ברוכה הבאה אל הקיר. בקרוב יופיעו כאן ההזמנות שלך.'
                  )}
                </p>
                <div className="pa-wall-hero__stats">
                  <div>
                    <div className="pa-wall-stat__num">
                      {ordersLoading
                        ? <span className="skeleton-line" style={{ display: 'inline-block', width: 44, height: 26 }} />
                        : heroStats.ordersCount}
                    </div>
                    <div className="pa-wall-stat__lab">הזמנות</div>
                  </div>
                  <div>
                    <div className="pa-wall-stat__num">
                      {ordersLoading
                        ? <span className="skeleton-line" style={{ display: 'inline-block', width: 58, height: 26 }} />
                        : heroStats.printsCount}
                    </div>
                    <div className="pa-wall-stat__lab">בלנדס</div>
                  </div>
                  <div>
                    <div className="pa-wall-stat__num">
                      {ordersLoading
                        ? <span className="skeleton-line" style={{ display: 'inline-block', width: 64, height: 26 }} />
                        : heroStats.memberSince}
                    </div>
                    <div className="pa-wall-stat__lab">חברה מאז</div>
                  </div>
                </div>
              </div>

              {ordersLoading ? (
                <div className="pa-wall pa-wall--skeleton" aria-hidden="true">
                  {WALL_POSITIONS.map((pos, i) => (
                    <div
                      key={i}
                      className={`pa-wall__photo pa-wall__photo--${pos} pa-wall__photo--skeleton`}
                    />
                  ))}
                </div>
              ) : wallPhotos.length > 0 ? (
                <div className="pa-wall">
                  {wallPhotos.map((p, i) => (
                    <div
                      key={i}
                      className={`pa-wall__photo pa-wall__photo--${WALL_POSITIONS[i]}`}
                      data-frame={p.frame}
                      style={FRAME_BG[p.frame] ? { backgroundImage: `url(${FRAME_BG[p.frame]})` } : undefined}
                    >
                      <img src={p.src} alt="" loading="lazy" />
                    </div>
                  ))}
                </div>
              ) : null}
            </section>

            {/* Strip — loyalty card (right in RTL) + quick actions (left) */}
            <section className="pa-strip">
              <div className="bm-card">
                <div className="bm-card__content">
                  <div className="bm-card__info">
                    <div className="bm-card__badge">Moments+</div>
                    <div className="bm-card__balance-wrap">
                      {creditsLoading ? (
                        <span className="bm-card__amount-skel" aria-hidden="true" />
                      ) : (
                        <>
                          <span className="bm-card__currency">&#8362;</span>
                          <span className="bm-card__amount">{creditsBalance.toFixed(2)}</span>
                        </>
                      )}
                    </div>
                    <div className="bm-card__earn-info">
                      <svg
                        className="bm-card__earn-icon"
                        width="13" height="13" viewBox="0 0 24 24"
                        fill="none" stroke="currentColor" strokeWidth="2.5"
                        strokeLinecap="round" strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      <span>הצבירה שלך זמינה למימוש</span>
                    </div>
                  </div>

                  <div className="bm-card__mascot">
                    <img
                      src="/assets/images/blends-moments-mascot.svg"
                      alt="Blends Moments"
                      className="bm-card__mascot-img"
                    />
                  </div>
                </div>
              </div>

              <div className="pa-quick-actions">
                <Link to="/upload-your-image" className="pa-quick-action">
                  <span className="pa-quick-action__icon pa-quick-action__icon--1">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                      <circle cx="8.5" cy="8.5" r="1.5"/>
                      <polyline points="21 15 16 10 5 21"/>
                    </svg>
                  </span>
                  <span className="pa-quick-action__label">הזמנה חדשה</span>
                </Link>
                <button
                  type="button"
                  className="pa-quick-action"
                  onClick={() => {
                    setAboutUs('CQ');
                    modal('open', width > 768 ? 'aboutUs' : 'aboutUsMobile');
                  }}
                >
                  <span className="pa-quick-action__icon pa-quick-action__icon--2">
                    <img src="/assets/images/inmenufaq.png" alt="" />
                  </span>
                  <span className="pa-quick-action__label">שאלות נפוצות</span>
                </button>
                <a
                  href="https://wa.me/972504993895"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pa-quick-action"
                >
                  <span className="pa-quick-action__icon pa-quick-action__icon--3">
                    <img src="/assets/images/inmenuwtsap.png" alt="" />
                  </span>
                  <span className="pa-quick-action__label">דברו איתנו בוואטסאפ</span>
                </a>
                <Link to="/" className="pa-quick-action">
                  <span className="pa-quick-action__icon pa-quick-action__icon--4">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                      <polyline points="9 22 9 12 15 12 15 22"/>
                    </svg>
                  </span>
                  <span className="pa-quick-action__label">עמוד הבית</span>
                </Link>
              </div>
            </section>
          </>
        )}

        {view === 'list' ? (
          <OrderList
            orders={orders}
            loading={ordersLoading}
            error={ordersError}
            onRetry={() => fetchOrders(auth.token, getDaysFromFilter(dateFilter))}
            onSelectOrder={handleSelectOrder}
            dateFilter={dateFilter}
            onDateFilterChange={setDateFilter}
          />
        ) : (
          <OrderDetail
            orderId={selectedOrderId}
            token={auth.token}
            fetchOrderDetail={getPersonalOrderDetail}
            onBack={handleBackToList}
          />
        )}
      </div>
    </div>
  );
};

export default PersonalArea;
